import type { ThemePreference } from "@tw-pulse/shared";
import { useTheme } from "@/lib/theme";
import { MoonIcon, MonitorIcon, SunIcon } from "@/lib/icons";

/**
 * Three-state segmented control for theme preference. Lives inside the user
 * menu in the sidebar (authenticated UI only — anonymous users still get the
 * resolved theme via the FOUC script).
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="flex items-center gap-0.5 rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-950/70"
    >
      <Option active={theme === "light"} label="Light" onSelect={() => setTheme("light")}>
        <SunIcon size={13} />
      </Option>
      <Option active={theme === "system"} label="System" onSelect={() => setTheme("system")}>
        <MonitorIcon size={13} />
      </Option>
      <Option active={theme === "dark"} label="Dark" onSelect={() => setTheme("dark")}>
        <MoonIcon size={13} />
      </Option>
    </div>
  );
}

function Option({
  active,
  label,
  onSelect,
  children,
}: {
  active: boolean;
  label: string;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      title={label}
      onClick={onSelect}
      className={
        "flex h-6 flex-1 items-center justify-center rounded transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " +
        (active
          ? "bg-white text-zinc-900 shadow-card-light dark:bg-zinc-800 dark:text-zinc-50 dark:shadow-none"
          : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-500 dark:hover:text-zinc-200")
      }
    >
      {children}
    </button>
  );
}

// Re-export to keep the type close to the consumer for clarity.
export type { ThemePreference };
